/**
 * Editing a note in place.
 *
 * The editor is the note: the same white card with its pale blue edge and
 * shadow, at the card's size and position, in the note font, so writing in it
 * looks like writing on the note and nothing jumps when it closes. It is a
 * contentEditable box rather than a text area so bold, italic, underline,
 * strikethrough and lists show as they will be drawn.
 *
 * What it holds is read back through `noteFromElement`, which keeps text and
 * those marks only; pasting inserts plain text. The box can be resized from
 * its corner, and the size it is left at is saved with the note.
 */
import { forwardRef, useEffect, useImperativeHandle, useRef, type CSSProperties } from "react";
import { noteFromElement, noteToHtml } from "../design/noteText";
import { MAX_NOTE_TEXT } from "../design/document";
import { THEME } from "../render/theme";
import { NOTE_METRICS, noteFont } from "../scene/notes";
import type { Size } from "../scene/types";

/** A formatting command the note's menu can run on the selection. */
export type NoteFormat = "bold" | "italic" | "underline" | "strikeThrough" | "insertUnorderedList" | "insertOrderedList";

export interface NoteEditorHandle {
  format: (command: NoteFormat) => void;
  /** Finish editing, saving what is written. */
  commit: () => void;
}

interface NoteEditorProps {
  initial: string;
  label: string;
  describedBy: string;
  /** Screen position of the card's top-left corner. */
  x: number;
  y: number;
  /** The card's size in scene units: the editor opens at exactly this. */
  size: Size;
  zoom: number;
  onCommit: (text: string, size: Size) => void;
  onCancel: () => void;
}

/** Whether focus is going to the note's own menu, which must not end editing. */
function toMenu(target: EventTarget | null): boolean {
  return Boolean((target as Element | null)?.closest?.('[role="menu"]'));
}

export const NoteEditor = forwardRef<NoteEditorHandle, NoteEditorProps>(function NoteEditor(
  { initial, label, describedBy, x, y, size, zoom, onCommit, onCancel },
  handle,
) {
  const boxRef = useRef<HTMLDivElement>(null);
  const done = useRef(false);
  const range = useRef<Range | null>(null);

  useEffect(() => {
    const box = boxRef.current;
    if (!box) return;
    // Generated here from the stored markup, escaped: never the stored text as HTML.
    box.innerHTML = initial ? noteToHtml(initial) : "<div><br></div>";
    box.focus();
    // Everything selected, as a label editor opens: typing replaces it.
    const selection = window.getSelection();
    if (selection) {
      const all = document.createRange();
      all.selectNodeContents(box);
      if (!initial) all.collapse(true);
      selection.removeAllRanges();
      selection.addRange(all);
      range.current = all.cloneRange();
    }
    try {
      // Marks as tags, not inline styles: what noteFromElement reads most plainly.
      document.execCommand?.("styleWithCSS", false, "false");
    } catch {
      // Not every browser has it; the reader copes with styles too.
    }
    // Once, on opening.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const remember = () => {
    const selection = window.getSelection();
    const box = boxRef.current;
    if (!selection || selection.rangeCount === 0 || !box) return;
    const current = selection.getRangeAt(0);
    if (box.contains(current.commonAncestorContainer)) range.current = current.cloneRange();
  };

  const finish = (commit: boolean) => {
    if (done.current) return;
    done.current = true;
    const box = boxRef.current;
    if (!commit || !box) {
      onCancel();
      return;
    }
    const text = noteFromElement(box).slice(0, MAX_NOTE_TEXT);
    // The box as left, back in scene units. Without layout (a test), as it opened.
    const width = box.offsetWidth > 0 ? Math.round(box.offsetWidth / zoom) : size.width;
    const height = box.offsetHeight > 0 ? Math.round(box.offsetHeight / zoom) : size.height;
    onCommit(text, { width, height });
  };

  const format = (command: NoteFormat) => {
    const box = boxRef.current;
    if (!box) return;
    box.focus();
    const selection = window.getSelection();
    if (selection && range.current) {
      selection.removeAllRanges();
      selection.addRange(range.current);
    }
    document.execCommand?.(command, false);
    remember();
  };

  useImperativeHandle(handle, () => ({ format, commit: () => finish(true) }));

  const { fontSize, lineHeight, padding, border, indent } = NOTE_METRICS;
  const style: CSSProperties & Record<string, string | number> = {
    left: x,
    top: y,
    width: size.width * zoom,
    minHeight: size.height * zoom,
    padding: padding * zoom,
    borderWidth: Math.max(1, border * zoom),
    borderColor: THEME.noteEdge,
    background: THEME.note,
    color: THEME.ink,
    font: noteFont({}, fontSize * zoom),
    lineHeight: `${lineHeight * zoom}px`,
    // Read by the stylesheet, for list indents in step with the drawing.
    "--note-indent": `${indent * zoom}px`,
  };

  return (
    <div
      ref={boxRef}
      data-label-editor=""
      data-note-editor=""
      className="cx-note-editor"
      role="textbox"
      aria-multiline="true"
      aria-label={label}
      aria-describedby={describedBy}
      contentEditable
      suppressContentEditableWarning
      spellCheck
      style={style}
      onKeyDown={(event) => {
        const mod = event.metaKey || event.ctrlKey;
        if (event.key === "Escape") {
          event.preventDefault();
          event.stopPropagation();
          finish(false);
        } else if (event.key === "Enter" && mod) {
          event.preventDefault();
          finish(true);
        } else if (mod && event.shiftKey && event.key.toLowerCase() === "x") {
          // Strikethrough has no browser shortcut of its own.
          event.preventDefault();
          format("strikeThrough");
        } else if (mod && event.shiftKey && (event.key === "8" || event.key === "*")) {
          event.preventDefault();
          format("insertUnorderedList");
        } else if (mod && event.shiftKey && (event.key === "7" || event.key === "&")) {
          event.preventDefault();
          format("insertOrderedList");
        }
      }}
      onKeyUp={remember}
      onMouseUp={remember}
      onInput={remember}
      onPaste={(event) => {
        // Plain text only: formatting from elsewhere is not the note's.
        event.preventDefault();
        const text = event.clipboardData.getData("text/plain");
        document.execCommand?.("insertText", false, text);
      }}
      onDrop={(event) => event.preventDefault()}
      onBlur={(event) => {
        remember();
        if (toMenu(event.relatedTarget)) return;
        finish(true);
      }}
    />
  );
});
